import { formatDateTime, formatPhone } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import { PasswordInput } from '../../components/auth/password-input.tsx';
import { PasswordStrength } from '../../components/auth/password-strength.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button, buttonVariants } from '../../components/ui/button.tsx';
import { RegisterRequestDialog, RequestBriefList } from '../../components/privacy/requests.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Field, Input } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { keys, useApiMutation, useMySessions, useTrustedDevices } from '../../lib/admin-api.ts';
import { api, ApiError, errorMessage } from '../../lib/api.ts';
import { describeUserAgent } from '../../lib/permission-texts.ts';
import { useMyRequests } from '../../lib/privacy-requests-api.ts';
import { SESSION_KEY, useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';

export function AccountPage() {
  const { data: session } = useSession();
  if (!session) return null;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Mi cuenta</h1>
        <p className="text-sm text-muted-foreground">
          {session.user.fullName} · {session.user.email} ·{' '}
          {session.membership.role?.name ?? 'Sin rol'}
        </p>
      </div>
      {!session.impersonation && <WorkPhoneCard current={session.membership.workPhone} />}
      {!session.impersonation && <DailySummaryCard on={session.membership.dailySummary} />}
      {!session.impersonation && <MyRequestsCard />}
      <SessionsCard />
      {!session.impersonation && <TrustedDevicesCard />}
      {session.impersonation ? (
        <Alert tone="info">
          Mientras actúas como otra persona no puedes cambiar su contraseña ni su verificación en
          dos pasos.
        </Alert>
      ) : (
        <>
          <ChangePasswordCard
            email={session.user.email}
            name={session.user.fullName}
            tenant={session.tenant.name}
          />
          <TwoFactorCard enrolled={session.mfaEnrolled} />
        </>
      )}
    </div>
  );
}

function SessionsCard() {
  const { data: session } = useSession();
  const sessions = useMySessions();
  // Not during an impersonation (the API refuses it): then nothing to forget here.
  const devices = useTrustedDevices(!session?.impersonation);
  const notify = useToast();
  // Checked by default: whoever closes the other sessions usually wants them all out.
  const [forgetDevices, setForgetDevices] = useState(true);
  const revoke = useApiMutation(
    (id: string) => api(`/auth/sessions/${id}`, { method: 'DELETE' }),
    [keys.mySessions],
  );
  const revokeOthers = useApiMutation(
    (forget: boolean) =>
      api<{ revoked: number; devicesForgotten: number }>('/auth/sessions/revoke-others', {
        method: 'POST',
        body: { forgetDevices: forget },
      }),
    [keys.mySessions, keys.trustedDevices],
  );
  const tz = session?.tenant.timezone;
  const otherSessions = (sessions.data?.length ?? 0) > 1;
  const remembered = (devices.data?.length ?? 0) > 0;

  return (
    <Card className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <h2 className="font-semibold">Mis sesiones activas</h2>
        <div className="flex flex-col gap-2 sm:items-end">
          <Button
            size="sm"
            variant="secondary"
            loading={revokeOthers.isPending}
            disabled={!otherSessions && !(forgetDevices && remembered)}
            onClick={() => {
              revokeOthers.mutate(forgetDevices, {
                onSuccess: (r) => {
                  const closed =
                    r.revoked === 1
                      ? 'Cerramos 1 sesión'
                      : `Cerramos ${String(r.revoked)} sesiones`;
                  const forgotten =
                    r.devicesForgotten === 0
                      ? ''
                      : r.devicesForgotten === 1
                        ? ' y olvidamos 1 dispositivo'
                        : ` y olvidamos ${String(r.devicesForgotten)} dispositivos`;
                  notify(`${closed}${forgotten}.`, 'success');
                },
              });
            }}
          >
            Cerrar todas las demás
          </Button>
          {!session?.impersonation && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-5"
                checked={forgetDevices}
                onChange={(event) => {
                  setForgetDevices(event.target.checked);
                }}
              />
              Olvidar también los dispositivos recordados
            </label>
          )}
        </div>
      </div>
      {sessions.isPending ? (
        <Loading />
      ) : sessions.error ? (
        <Alert tone="danger">{errorMessage(sessions.error)}</Alert>
      ) : (
        <ul className="divide-y divide-border">
          {sessions.data.map((s) => (
            <li key={s.id} className="flex items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {describeUserAgent(s.userAgent)}{' '}
                  {s.current && <Badge tone="info">Esta sesión</Badge>}
                  {s.impersonation && <Badge tone="warning">Abierta por un administrador</Badge>}
                </p>
                <p className="text-sm text-muted-foreground">
                  IP {s.ip ?? 'desconocida'} · Última actividad {formatDateTime(s.lastSeenAt, tz)}
                </p>
              </div>
              {!s.current && (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Cerrar sesión de ${describeUserAgent(s.userAgent)}`}
                  onClick={() => {
                    revoke.mutate(s.id, {
                      onSuccess: () => {
                        notify('Sesión cerrada.', 'success');
                      },
                    });
                  }}
                >
                  Cerrar
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** "Recordar este dispositivo": the browsers that skip the 2FA code. */
function TrustedDevicesCard() {
  const { data: session } = useSession();
  const devices = useTrustedDevices();
  const notify = useToast();
  const forget = useApiMutation(
    (id: string) => api(`/auth/trusted-devices/${id}`, { method: 'DELETE' }),
    [keys.trustedDevices],
  );
  const forgetAll = useApiMutation(
    () => api<{ forgotten: number }>('/auth/trusted-devices/forget-all', { method: 'POST' }),
    [keys.trustedDevices],
  );
  const tz = session?.tenant.timezone;

  return (
    <Card className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-semibold">Dispositivos recordados</h2>
          <p className="text-sm text-muted-foreground">
            En estos navegadores no te pedimos el código de verificación al iniciar sesión (sí la
            contraseña).
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          loading={forgetAll.isPending}
          disabled={(devices.data?.length ?? 0) === 0}
          onClick={() => {
            forgetAll.mutate(undefined, {
              onSuccess: () => {
                notify('Olvidamos todos tus dispositivos.', 'success');
              },
            });
          }}
        >
          Olvidar todos
        </Button>
      </div>
      {devices.isPending ? (
        <Loading />
      ) : devices.error ? (
        <Alert tone="danger">{errorMessage(devices.error)}</Alert>
      ) : devices.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Ninguno. Al escribir el código puedes marcar «Recordar este dispositivo».
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {devices.data.map((d) => (
            <li key={d.id} className="flex items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {describeUserAgent(d.userAgent)}{' '}
                  {d.current && <Badge tone="info">Este navegador</Badge>}
                </p>
                <p className="text-sm text-muted-foreground">
                  Recordado el {formatDateTime(d.createdAt, tz)} · Último uso{' '}
                  {d.lastUsedAt ? formatDateTime(d.lastUsedAt, tz) : 'aún no'} · Vence el{' '}
                  {formatDateTime(d.expiresAt, tz)}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Olvidar ${describeUserAgent(d.userAgent)}`}
                onClick={() => {
                  forget.mutate(d.id, {
                    onSuccess: () => {
                      notify('Dispositivo olvidado.', 'success');
                    },
                  });
                }}
              >
                Olvidar
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ChangePasswordCard({
  email,
  name,
  tenant,
}: {
  email: string;
  name: string;
  tenant: string;
}) {
  const notify = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<{ current?: string; next?: string; general?: string }>({});
  const change = useApiMutation(
    (body: { currentPassword: string; newPassword: string }) =>
      api('/auth/password', { method: 'POST', body }),
    [keys.mySessions],
  );

  return (
    <Card className="space-y-4">
      <h2 className="font-semibold">Cambiar contraseña</h2>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (next !== repeat) {
            setErrors({ next: 'Las contraseñas nuevas no coinciden.' });
            return;
          }
          setErrors({});
          change.mutate(
            { currentPassword: current, newPassword: next },
            {
              onSuccess: () => {
                setCurrent('');
                setNext('');
                setRepeat('');
                notify('Contraseña cambiada. Cerramos tus otras sesiones.', 'success');
              },
              onError: (err) => {
                if (err instanceof ApiError && err.code === 'wrong_current_password')
                  setErrors({ current: err.message });
                else if (err instanceof ApiError && err.code === 'weak_password')
                  setErrors({ next: err.message });
                else setErrors({ general: errorMessage(err) });
              },
            },
          );
        }}
      >
        {errors.general && <Alert tone="danger">{errors.general}</Alert>}
        <Field label="Contraseña actual" error={errors.current}>
          {(a11y) => (
            <PasswordInput
              {...a11y}
              autoComplete="current-password"
              value={current}
              onChange={(e) => {
                setCurrent(e.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Contraseña nueva" error={errors.next}>
          {(a11y) => (
            <PasswordInput
              {...a11y}
              autoComplete="new-password"
              value={next}
              onChange={(e) => {
                setNext(e.target.value);
              }}
            />
          )}
        </Field>
        <PasswordStrength password={next} userInputs={[email, name, tenant]} />
        <Field label="Repite la contraseña nueva">
          {(a11y) => (
            <PasswordInput
              {...a11y}
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => {
                setRepeat(e.target.value);
              }}
            />
          )}
        </Field>
        <Button
          variant="brand"
          type="submit"
          loading={change.isPending}
          disabled={!current || !next}
        >
          Cambiar contraseña
        </Button>
      </form>
    </Card>
  );
}

function TwoFactorCard({ enrolled }: { enrolled: boolean }) {
  const [codes, setCodes] = useState<string[] | null>(null);
  const regenerate = useApiMutation(
    () => api<{ recoveryCodes: string[] }>('/auth/2fa/recovery-codes', { method: 'POST' }),
    [],
  );
  return (
    <Card className="space-y-4">
      <h2 className="font-semibold">Verificación en dos pasos</h2>
      {enrolled ? (
        <>
          <p className="text-sm">
            <Badge tone="success">Activada</Badge> Usas una aplicación de autenticación al iniciar
            sesión.
          </p>
          {codes ? (
            <Alert
              tone="warning"
              title="Tus nuevos códigos de respaldo (los anteriores ya no sirven)"
            >
              <ul
                aria-label="Códigos de respaldo nuevos"
                className="mt-2 grid grid-cols-2 gap-1 font-mono"
              >
                {codes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </Alert>
          ) : (
            <Button
              variant="secondary"
              loading={regenerate.isPending}
              onClick={() => {
                regenerate.mutate(undefined, {
                  onSuccess: (r) => {
                    setCodes(r.recoveryCodes);
                  },
                });
              }}
            >
              Generar códigos de respaldo nuevos
            </Button>
          )}
          {regenerate.error && <Alert tone="danger">{errorMessage(regenerate.error)}</Alert>}
        </>
      ) : (
        <>
          <p className="text-sm">
            <Badge tone="warning">Desactivada</Badge> Actívala para proteger tu cuenta aunque
            alguien conozca tu contraseña.
          </p>
          <Link
            to="/activar-2fa?volver=%2Fmi-cuenta"
            className={buttonVariants({ variant: 'primary' })}
          >
            Activar verificación en dos pasos
          </Link>
        </>
      )}
    </Card>
  );
}

/** The phone clients see on shared listings and use for WhatsApp. */
function WorkPhoneCard({ current }: { current: string | null }) {
  const client = useQueryClient();
  const notify = useToast();
  const [value, setValue] = useState(current ? formatPhone(current) : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api('/auth/profile', { method: 'PATCH', body: { workPhone: value } });
      await client.invalidateQueries({ queryKey: SESSION_KEY });
      notify(
        value.trim() ? 'Teléfono de trabajo guardado.' : 'Teléfono de trabajo quitado.',
        'success',
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="space-y-4">
      <div>
        <h2 className="font-semibold">Teléfono de trabajo</h2>
        <p className="text-sm text-muted-foreground">
          Lo ven tus clientes en las fichas que compartes y en el botón de WhatsApp. Si lo dejas
          vacío se usa el teléfono general de la inmobiliaria.
        </p>
      </div>
      <form
        className="flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="flex-1">
          <Field label="Teléfono" error={error ?? undefined}>
            {(a11y) => (
              <Input
                {...a11y}
                type="tel"
                inputMode="tel"
                placeholder="7123 4567"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <Button type="submit" variant="secondary" loading={busy}>
          Guardar
        </Button>
      </form>
    </Card>
  );
}

/** HU-AGE-13: the 07:00 email (Monday to Saturday) with the shape of the day. */
function DailySummaryCard({ on }: { on: boolean }) {
  const client = useQueryClient();
  const notify = useToast();
  const [busy, setBusy] = useState(false);
  const toggle = async (value: boolean) => {
    setBusy(true);
    try {
      await api('/auth/profile', { method: 'PATCH', body: { dailySummary: value } });
      await client.invalidateQueries({ queryKey: SESSION_KEY });
      notify(
        value ? 'Recibirás el resumen diario.' : 'Ya no recibirás el resumen diario.',
        'success',
      );
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="space-y-3">
      <h2 className="font-semibold">Resumen diario</h2>
      <label className="flex min-h-11 items-start gap-3 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 size-5"
          checked={on}
          disabled={busy}
          onChange={(e) => void toggle(e.target.checked)}
        />
        <span>
          <span className="font-medium">Recibir el resumen de mi día</span>
          <span className="block text-muted-foreground">
            De lunes a sábado a las 7:00: cuántas actividades tienes hoy, cuáles se vencieron y qué
            negocios no tienen próxima actividad. Llega a la campana y por correo, sin datos de
            clientes.
          </span>
        </span>
      </label>
    </Card>
  );
}

/** Requests on personal data I registered: their status (privacy handles them). */
function MyRequestsCard() {
  const requests = useMyRequests();
  const [registering, setRegistering] = useState(false);
  return (
    <Card className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold">Solicitudes sobre datos personales</h2>
          <p className="text-sm text-muted-foreground">
            Si alguien te pide ver, corregir, borrar u oponerse al uso de sus datos, regístralo aquí
            el mismo día: corre un plazo legal. Te avisamos cuando se resuelva.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            setRegistering(true);
          }}
        >
          Registrar solicitud
        </Button>
      </div>
      <RequestBriefList items={requests.data ?? []} empty="No has registrado ninguna." />
      {registering && (
        <RegisterRequestDialog
          onClose={() => {
            setRegistering(false);
          }}
        />
      )}
    </Card>
  );
}
